import { useEffect } from "react";

import { readJSON, writeJSON, storageKeyFor } from "~/shared/lib/storage";
import { useChatSettingsStore, type ChatSettingsState } from "~/shared/store/useChatSettingsStore";

type PersistedSettings = Pick<
    ChatSettingsState,
    | "artifactType"
    | "modelId"
    | "mode"
    | "aspectRatio"
    | "resolution"
    | "duration"
    | "audioEnabled"
    | "batchSize"
>;

function extractData(state: ChatSettingsState): PersistedSettings {
    return {
        artifactType: state.artifactType,
        modelId: state.modelId,
        mode: state.mode,
        aspectRatio: state.aspectRatio,
        resolution: state.resolution,
        duration: state.duration,
        audioEnabled: state.audioEnabled,
        batchSize: state.batchSize,
    };
}

const readStored = (chatId: string): PersistedSettings | null =>
    readJSON<PersistedSettings | null>(storageKeyFor.chatSettings(chatId), null);

const writeStored = (chatId: string, data: PersistedSettings): void =>
    writeJSON(storageKeyFor.chatSettings(chatId), data);

export function useChatSettingsPersist(chatId: string | null): void {
    useEffect(() => {
        if (!chatId) return;
        const saved = readStored(chatId);
        if (saved) {
            useChatSettingsStore.setState(saved);
        }
        const unsubscribe = useChatSettingsStore.subscribe((state) => {
            writeStored(chatId, extractData(state));
        });
        return unsubscribe;
    }, [chatId]);
}
